import { useRef, useEffect } from 'react';

import { Text, Flex, TextField, Button, Tooltip } from '@radix-ui/themes';
import { useTranslation } from 'react-i18next';

import styles from './banner.module.css';
import { useUrlForm } from './useUrlForm';
import { useNotification } from '../../../../shared/Notification/useNotification';

const RightSideSlot = ({ isDisabled, setIsEditing, isEditing }) => {
  const { t } = useTranslation('campaigns');
  return (
    <Flex>
      {isEditing ? (
        <Button
          variant="solid"
          type="submit"
          size="2"
          style={{ borderRadius: '0 var(--radius-3) var(--radius-3) 0' }}
          className={styles.urlInputButton}
        >
          <i className="fa-light fa-floppy-disk"></i> {t('urlInput.saveUrl')}
        </Button>
      ) : (
        <Button
          variant="outline"
          size="2"
          style={{ borderRadius: '0 var(--radius-3) var(--radius-3) 0' }}
          type="button"
          key="edit-button"
          disabled={isDisabled}
          className={styles.urlInputButton}
          onClick={() => {
            setIsEditing(true);
          }}
        >
          <i className="fa-light fa-pencil fa-sm" />
          {t('urlInput.editUrl')}
        </Button>
      )}
    </Flex>
  );
};

export const UrlInput = ({ campaign }) => {
  const inputRef = useRef(null);
  const { t } = useTranslation('campaigns');
  const { showSuccess } = useNotification();
  const isDisabled = campaign.isArchived || !!campaign.deletedAt;

  const {
    values,
    handleSubmit,
    errors,
    handleSlugChange,
    isEditing,
    setIsEditing,
  } = useUrlForm({
    id: campaign.uuid,
    slug: campaign.slug,
  });

  const { slug } = values;

  const shareLink = `${import.meta.env.VITE_APP_HOST}/board/${
    campaign.demoSlug
  }/`;

  const completeLink = `${shareLink}${slug}`;

  const copyHighlightLink = () => {
    navigator.clipboard.writeText(completeLink);
  };

  useEffect(() => {
    if (isEditing) {
      inputRef.current?.focus();
    }
  }, [isEditing]);

  return (
    <form
      onSubmit={e => {
        e.preventDefault();
        if (isEditing) {
          handleSubmit();
        }
      }}
    >
      <Flex
        align="start"
        direction="column"
        gap="3"
        className={styles.highlightContainer}
      >
        <Flex align="center" gap="3" width="100%">
          <Text size="2" className={styles.highlightLabel} weight="medium">
            {t('urlInput.highlightUrl')}:
            <Tooltip maxWidth="220px" content={t('urlInput.tooltip')}>
              <i
                className="fa-light fa-circle-info fa-sm"
                style={{ marginLeft: '4px' }}
              />
            </Tooltip>
          </Text>
          <Flex width="100%" className={styles.highlightUrlLink}>
            <TextField.Root
              className={`${styles.highlightInput} ${
                errors.slug ? styles.inputError : ''
              }`}
              onChange={e => handleSlugChange(e.target.value)}
              id="slug-form"
              ref={inputRef}
              disabled={!isEditing}
              value={isEditing ? slug : completeLink}
            >
              {isEditing && (
                <TextField.Slot pr="0" className={styles.baseLink} side="left">
                  <Text className={styles.baseLinkText}>{shareLink}</Text>
                </TextField.Slot>
              )}
              {campaign.deletedAt && (
                <TextField.Slot side="right" pr="0">
                  <Tooltip
                    maxWidth="220px"
                    content={t('urlInput.deletedTooltip')}
                  >
                    <Text className={`${styles.pill} ${styles.inactivePill}`}>
                      {t('urlInput.linkInactive')}
                      <i
                        className="fa-light fa-circle-info fa-sm"
                        style={{ marginLeft: '4px' }}
                      />
                    </Text>
                  </Tooltip>
                  <RightSideSlot
                    isDisabled={isDisabled}
                    setIsEditing={setIsEditing}
                    isEditing={isEditing}
                    copyHighlightLink={copyHighlightLink}
                  />
                </TextField.Slot>
              )}
              {!campaign.deletedAt && campaign.status === 'draft' && (
                <TextField.Slot side="right" pr="0">
                  <Tooltip
                    maxWidth="220px"
                    content={t('urlInput.draftTooltip')}
                  >
                    <Text className={`${styles.pill} ${styles.inactivePill}`}>
                      {t('urlInput.linkInactive')}
                      <i
                        className="fa-light fa-circle-info fa-sm"
                        style={{ marginLeft: '4px' }}
                      />
                    </Text>
                  </Tooltip>
                  <RightSideSlot
                    isDisabled={isDisabled}
                    setIsEditing={setIsEditing}
                    isEditing={isEditing}
                    copyHighlightLink={copyHighlightLink}
                  />
                </TextField.Slot>
              )}
              {!campaign.deletedAt && campaign.status === 'active' && (
                <TextField.Slot side="right" pr="0">
                  <Tooltip
                    maxWidth="220px"
                    content={t('urlInput.activeTooltip')}
                  >
                    <Text className={`${styles.pill} ${styles.activePill}`}>
                      {t('urlInput.linkActive')}
                      <i
                        className="fa-light fa-circle-info fa-sm"
                        style={{ marginLeft: '4px' }}
                      />
                    </Text>
                  </Tooltip>
                  <RightSideSlot
                    isDisabled={isDisabled}
                    setIsEditing={setIsEditing}
                    isEditing={isEditing}
                    copyHighlightLink={copyHighlightLink}
                  />
                </TextField.Slot>
              )}
              {!campaign.deletedAt && campaign.status === 'completed' && (
                <TextField.Slot side="right" pr="0">
                  <Tooltip
                    maxWidth="220px"
                    content={t('urlInput.completedTooltip')}
                  >
                    <Text className={`${styles.pill} ${styles.activePill}`}>
                      {t('urlInput.linkActive')}
                      <i
                        className="fa-light fa-circle-info fa-sm"
                        style={{ marginLeft: '4px' }}
                      />
                    </Text>
                  </Tooltip>
                  <RightSideSlot
                    isDisabled={isDisabled}
                    setIsEditing={setIsEditing}
                    isEditing={isEditing}
                    copyHighlightLink={copyHighlightLink}
                  />
                </TextField.Slot>
              )}
              {!campaign.deletedAt && campaign.status === 'archived' && (
                <TextField.Slot side="right" pr="0">
                  <Tooltip
                    maxWidth="220px"
                    content={t('urlInput.archivedTooltip')}
                  >
                    <Text className={`${styles.pill} ${styles.inactivePill}`}>
                      {t('urlInput.linkInactive')}
                      <i
                        className="fa-light fa-circle-info fa-sm"
                        style={{ marginLeft: '4px' }}
                      />
                    </Text>
                  </Tooltip>
                  <RightSideSlot
                    isDisabled={isDisabled}
                    setIsEditing={setIsEditing}
                    isEditing={isEditing}
                    copyHighlightLink={copyHighlightLink}
                  />
                </TextField.Slot>
              )}
            </TextField.Root>
          </Flex>
          <Button
            variant="outline"
            size="2"
            className={styles.urlInputButton}
            onClick={() => {
              copyHighlightLink();
              showSuccess({ content: t('urlInput.copyUrlSuccess') });
            }}
            type="button"
          >
            <i className="fa-light fa-copy fa-sm" />
            {t('urlInput.copyUrl')}
          </Button>
        </Flex>
        {errors.slug && (
          <Text size="2" weight="medium" className={styles.inputErrorText}>
            {errors.slug}
          </Text>
        )}
      </Flex>
    </form>
  );
};
